import api from './api';
import { ReportPeriod } from './reports.service';

export interface WalletOverview {
  total_wallets: number;
  wallets_activas: number;
  saldo_total: number;
  saldo_bonus_total: number;
  saldo_bloqueado_total: number;
  saldo_circulacao: number;
  total_carregado_all: number;
  total_gasto_all: number;
  total_taxas_all: number;
  periodo: {
    count_topups: number;
    montante_topups: number;
    montante_taxas: number;
    count_pagamentos: number;
    montante_pagamentos: number;
    count_transferencias: number;
    montante_transferencias: number;
  };
  tokens: {
    total: number;
    activos: number;
    inactivos: number;
    novos_periodo: number;
  };
  pedidos_tokenizacao: {
    total: number;
    pending: number;
    completed: number;
    falhados: number;
  };
}

export interface WalletActivityRow {
  label: string;
  topups: number;
  montante_topup: number;
  pagamentos: number;
  montante_pagamento: number;
  transferencias: number;
  montante_transferencia: number;
}

export interface WalletUser {
  wallet_id: number;
  user_id: string;
  nome: string;
  email: string;
  username: string;
  user_status: string;
  wallet_status: string;
  nivel: string;
  saldo: number;
  saldo_bonus: number;
  saldo_bloqueado: number;
  total_carregado: number;
  total_gasto: number;
  total_transacoes: number;
  wallet_criada: string;
}

export interface WalletUsersResponse {
  data: WalletUser[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

export interface CardEnrollment {
  merchantRef: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | string;
  posId: string;
  criadoEm: string;
  actualizadoEm: string | null;
  email: string;
  username: string;
}

export interface CardEnrollmentsResponse {
  data: CardEnrollment[];
  porEstado: Record<string, number>;
  pagination: { total: number; page: number; limit: number; pages: number };
}

export interface TokenUser {
  cliente_id: string | number;
  nome: string;
  email: string;
  username: string;
  num_tokens: number;
  num_activos: number;
  ultimo_uso: string | null;
  primeiro_token: string | null;
  tipos_cartao: string[];
}

export interface TokenUsersResponse {
  data: TokenUser[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

export interface TopupRow {
  id: string;
  referencia: string;
  valor_creditado: number;
  fee_cve: number;
  valor_bruto: number;
  instrument: string;
  status: string;
  created_at: string;
  email: string;
  username: string;
  nome: string;
}

export interface TopupsResponse {
  summary: {
    total_taxas: number;
    total_creditado: number;
    count_topups: number;
    /**
     * Cotações com o cartão já cobrado e a carteira por creditar.
     * `count` a zero é o estado normal; qualquer número acima disso é dinheiro
     * do cliente retido sem contrapartida.
     */
    por_creditar?: { count: number; total_cve: number; mais_antiga: string | null };
  };
  data: TopupRow[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

export interface WalletTokensBreakdown {
  by_card_type: { name: string; total: number; activos: number }[];
  new_over_time: { label: string; novos_tokens: number }[];
  req_over_time: { label: string; total: number; completed: number; pending: number }[];
  top_users: { cliente_id: string; nome: string; email: string; num_tokens: number; ultimo_uso: string | null }[];
}

class WalletStatsService {
  async getOverview(period: ReportPeriod = 'month'): Promise<{ success: boolean; data: WalletOverview }> {
    const response = await api.get(`/admin/reports/wallet/overview?period=${period}`);
    return response.data;
  }

  async getActivity(period: ReportPeriod = 'month'): Promise<{ success: boolean; data: WalletActivityRow[] }> {
    const response = await api.get(`/admin/reports/wallet/activity?period=${period}`);
    return response.data;
  }

  async getTokensBreakdown(period: ReportPeriod = 'month'): Promise<{ success: boolean; data: WalletTokensBreakdown }> {
    const response = await api.get(`/admin/reports/wallet/tokens-breakdown?period=${period}`);
    return response.data;
  }

  async getTokenUsers(params: { page?: number; limit?: number; search?: string } = {}): Promise<{ success: boolean } & TokenUsersResponse> {
    const q = new URLSearchParams();
    if (params.page)   q.set('page',   String(params.page));
    if (params.limit)  q.set('limit',  String(params.limit));
    if (params.search) q.set('search', params.search);
    const response = await api.get(`/admin/reports/wallet/token-users?${q.toString()}`);
    return response.data;
  }

  async getTopups(params: { page?: number; limit?: number; search?: string; period?: string } = {}): Promise<{ success: boolean } & TopupsResponse> {
    const q = new URLSearchParams();
    if (params.page)   q.set('page',   String(params.page));
    if (params.limit)  q.set('limit',  String(params.limit));
    if (params.search) q.set('search', params.search);
    if (params.period) q.set('period', params.period);
    const response = await api.get(`/admin/reports/wallet/topups?${q.toString()}`);
    return response.data;
  }

  async getCardEnrollments(params: { page?: number; limit?: number; search?: string; status?: string } = {}): Promise<{ success: boolean } & CardEnrollmentsResponse> {
    const q = new URLSearchParams();
    if (params.page)   q.set('page',   String(params.page));
    if (params.limit)  q.set('limit',  String(params.limit));
    if (params.search) q.set('search', params.search);
    if (params.status) q.set('status', params.status);
    const response = await api.get(`/admin/reports/wallet/card-enrollments?${q.toString()}`);
    return response.data;
  }

  async getUsers(params: { page?: number; limit?: number; search?: string; status?: string } = {}): Promise<{ success: boolean } & WalletUsersResponse> {
    const q = new URLSearchParams();
    if (params.page)   q.set('page',   String(params.page));
    if (params.limit)  q.set('limit',  String(params.limit));
    if (params.search) q.set('search', params.search);
    if (params.status) q.set('status', params.status);
    const response = await api.get(`/admin/reports/wallet/users?${q.toString()}`);
    return response.data;
  }
}

export default new WalletStatsService();
